/**
 * Dropdown problem input.
 */
import React, { useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";

import CorrectAnswerCard from "./CorrectAnswerCard";
import WrongAnswerCard from "./WrongAnswerCard";
import RenderResponsePrompt from "./RenderResponsePrompt";

/**
 * Select control built from `<optioninput>` choices.
 *
 * @param {{ tag: Element, onSetAnswer?: Function }} props
 */
export default function OptionResponseRender(props) {
  const { tag, grade, onSetAnswer, isShowMark, setShowMark } = props;

  const [options, setOptions] = useState([]);
  const [selection, setSelection] = useState("");
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(false);

  useEffect(() => {
    if (!tag) return;

    const optionInput = tag.querySelector("optioninput");
    if (!optionInput) return;

    let choices = optionInput.getAttribute("options") || "[]";

    // options come like: "('A','B')" or "['A','B']" in your current parsing logic
    choices = choices.substring(1, choices.length - 1).trim();
    const parts = choices ? choices.split(",") : [];
    const mapped = parts
      .map((c) => c.trim())
      .filter(Boolean)
      .map((c) => {
        // remove wrapping quotes if any
        const s = c.trim();
        const unquoted =
          (s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))
            ? s.slice(1, -1)
            : s;
        return { value: unquoted, text: unquoted };
      });

    const correct = optionInput.getAttribute("correct");
    setOptions(mapped);
    setCorrectAnswer(correct);

    // default selection
    const initial = mapped.length ? mapped[0].value : "";
    setSelection((prev) => prev || initial);
  }, [tag]);

  /**
   * Grades the selected option and reports the answer.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} e Change event.
   * @returns {void}
   */
  const onChange = (e) => {
    setShowMark(false);

    const val = e.target.value;
    setSelection(val);

    const correct = val === correctAnswer;

    onSetAnswer({
      id: grade?.id,
      student_answer: val,
      correctness: correct ? "correct" : "incorrect",
    });

    setIsCorrect(correct);
  };

  return (
    <div className="mb-2">
      <RenderResponsePrompt tag={tag} />

      <FormControl fullWidth size="small">
        <InputLabel id={`${grade?.id || "option"}-label`}>Select</InputLabel>
        <Select
          labelId={`${grade?.id || "option"}-label`}
          value={selection}
          label="Select"
          onChange={onChange}
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.text}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isShowMark && (isCorrect ? <CorrectAnswerCard /> : <WrongAnswerCard />)}
    </div>
  );
}
